import {
  Banknote,
  CalendarClock,
  CheckCircle2,
  FilePlus2,
  Plus,
  RefreshCcw,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';

const monthValue = () => new Date().toISOString().slice(0, 7);

const categories = [
  '정기점검',
  '승강기',
  '소방',
  '전기안전',
  '청소',
  '보안',
  '공용전기',
  '공용수도',
  '방역',
  '수리',
  '소모품',
  '기타',
];

const intervalOptions = [
  { value: 'monthly', label: '매월' },
  { value: 'quarterly', label: '분기' },
  { value: 'semiannual', label: '반기' },
  { value: 'annual', label: '연간' },
];

const statusLabels = {
  scheduled: '예정',
  pending: '미지급',
  paid: '지급완료',
  hold: '보류',
};

const formatWon = (value) => `${Number(value || 0).toLocaleString()}원`;

const BuildingOperatingExpenses = () => {
  const queryClient = useQueryClient();
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(monthValue());
  const [contractFormOpen, setContractFormOpen] = useState(false);
  const [expenseFormOpen, setExpenseFormOpen] = useState(false);

  const { data: buildingsData } = useQuery('operating-expense-buildings', async () => {
    const response = await api.get('/rmc/operating-expenses/buildings');
    return response;
  });

  const buildings = useMemo(() => buildingsData?.buildings || [], [buildingsData]);
  const selectedBuilding = useMemo(() => {
    const targetId = Number(selectedBuildingId || buildings[0]?.id);
    return buildings.find((building) => building.id === targetId) || null;
  }, [buildings, selectedBuildingId]);

  const effectiveBuildingId = selectedBuilding?.id || '';

  const contractsQuery = useQuery(
    ['operating-contracts', effectiveBuildingId],
    async () => {
      const response = await api.get('/rmc/operating-expenses/contracts', {
        params: { building_id: effectiveBuildingId },
      });
      return response;
    },
    { enabled: Boolean(effectiveBuildingId) }
  );

  const expensesQuery = useQuery(
    ['operating-expenses', effectiveBuildingId, selectedMonth],
    async () => {
      const response = await api.get('/rmc/operating-expenses/expenses', {
        params: { building_id: effectiveBuildingId, expense_month: selectedMonth },
      });
      return response;
    },
    { enabled: Boolean(effectiveBuildingId) }
  );

  const summaryQuery = useQuery(
    ['operating-expense-summary', effectiveBuildingId, selectedMonth],
    async () => {
      const response = await api.get('/rmc/operating-expenses/summary', {
        params: { building_id: effectiveBuildingId, expense_month: selectedMonth },
      });
      return response;
    },
    { enabled: Boolean(effectiveBuildingId) }
  );

  const invalidate = () => {
    queryClient.invalidateQueries(['operating-contracts', effectiveBuildingId]);
    queryClient.invalidateQueries(['operating-expenses', effectiveBuildingId, selectedMonth]);
    queryClient.invalidateQueries(['operating-expense-summary', effectiveBuildingId, selectedMonth]);
  };

  const createContractMutation = useMutation(
    (payload) => api.post('/rmc/operating-expenses/contracts', payload),
    { onSuccess: () => { setContractFormOpen(false); invalidate(); } }
  );

  const createExpenseMutation = useMutation(
    (payload) => api.post('/rmc/operating-expenses/expenses', payload),
    { onSuccess: () => { setExpenseFormOpen(false); invalidate(); } }
  );

  const generateMutation = useMutation(
    () => api.post('/rmc/operating-expenses/contracts/generate-expenses', {
      building_id: effectiveBuildingId,
      billing_month: selectedMonth,
    }),
    { onSuccess: invalidate }
  );

  const markPaidMutation = useMutation(
    (expense) => api.patch(`/rmc/operating-expenses/expenses/${expense.id}`, {
      payment_status: 'paid',
      paid_date: new Date().toISOString().slice(0, 10),
    }),
    { onSuccess: invalidate }
  );

  const contracts = contractsQuery.data?.contracts || [];
  const expenses = expensesQuery.data?.expenses || [];
  const summary = summaryQuery.data || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">건물 운영비관리</h1>
          <p className="mt-1 text-sm text-gray-600">
            건물별 정기 운영계약과 월별 비용 집행을 관리합니다.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setContractFormOpen(true)}
            className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
            disabled={!effectiveBuildingId}
          >
            <CalendarClock className="mr-2 h-4 w-4" />
            정기계약
          </button>
          <button
            type="button"
            onClick={() => setExpenseFormOpen(true)}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={!effectiveBuildingId}
          >
            <FilePlus2 className="mr-2 h-4 w-4" />
            비용등록
          </button>
          <button
            type="button"
            onClick={() => generateMutation.mutate()}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={!effectiveBuildingId || generateMutation.isLoading}
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            예정비용 생성
          </button>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px]">
        <select
          value={effectiveBuildingId}
          onChange={(event) => setSelectedBuildingId(event.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          {buildings.map((building) => (
            <option key={building.id} value={building.id}>
              {building.name} · {building.management_operation_type === 'maintenance_revenue_rmc' ? '관리비 책임운영형' : '일반 위탁'}
            </option>
          ))}
        </select>
        <input
          type="month"
          value={selectedMonth}
          onChange={(event) => setSelectedMonth(event.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      {!effectiveBuildingId ? (
        <div className="rounded-md border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          관리 중인 건물이 없습니다.
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <SummaryTile icon={Banknote} label="총 운영비" value={formatWon(summary.total_expense)} />
            <SummaryTile icon={CheckCircle2} label="지급완료" value={formatWon(summary.paid_expense)} />
            <SummaryTile icon={CalendarClock} label="예정/미지급" value={formatWon(summary.scheduled_expense)} />
            <SummaryTile icon={FilePlus2} label="등록건수" value={`${summary.expense_count || 0}건`} />
          </div>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">정기 운영계약</h2>
            <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3">계약</th>
                    <th className="px-4 py-3">업체</th>
                    <th className="px-4 py-3">주기</th>
                    <th className="px-4 py-3 text-right">금액</th>
                    <th className="px-4 py-3">상태</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {contracts.map((contract) => (
                    <tr key={contract.id}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{contract.title}</div>
                        <div className="text-xs text-gray-500">{contract.category}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{contract.vendor_name}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {intervalOptions.find((item) => item.value === contract.recurring_interval)?.label || contract.recurring_interval}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">{formatWon(contract.total_amount)}</td>
                      <td className="px-4 py-3 text-gray-700">{contract.status === 'active' ? '진행중' : '종료'}</td>
                    </tr>
                  ))}
                  {contracts.length === 0 && (
                    <tr>
                      <td className="px-4 py-8 text-center text-gray-500" colSpan="5">
                        등록된 정기 운영계약이 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">월별 비용 집행</h2>
            <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3">비용</th>
                    <th className="px-4 py-3">업체</th>
                    <th className="px-4 py-3">예정/지급일</th>
                    <th className="px-4 py-3 text-right">금액</th>
                    <th className="px-4 py-3">상태</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {expenses.map((expense) => (
                    <tr key={expense.id}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{expense.title}</div>
                        <div className="text-xs text-gray-500">{expense.category}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{expense.vendor_name || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{expense.paid_date || expense.due_date || expense.expense_date}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">{formatWon(expense.total_amount)}</td>
                      <td className="px-4 py-3 text-gray-700">{statusLabels[expense.payment_status] || expense.payment_status}</td>
                      <td className="px-4 py-3 text-right">
                        {expense.payment_status !== 'paid' && (
                          <button
                            type="button"
                            onClick={() => markPaidMutation.mutate(expense)}
                            className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          >
                            지급완료
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {expenses.length === 0 && (
                    <tr>
                      <td className="px-4 py-8 text-center text-gray-500" colSpan="6">
                        해당 월에 등록된 운영비가 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {contractFormOpen && (
        <ContractModal
          buildingId={effectiveBuildingId}
          onClose={() => setContractFormOpen(false)}
          onSubmit={(payload) => createContractMutation.mutate(payload)}
          isSaving={createContractMutation.isLoading}
        />
      )}

      {expenseFormOpen && (
        <ExpenseModal
          buildingId={effectiveBuildingId}
          selectedMonth={selectedMonth}
          onClose={() => setExpenseFormOpen(false)}
          onSubmit={(payload) => createExpenseMutation.mutate(payload)}
          isSaving={createExpenseMutation.isLoading}
        />
      )}
    </div>
  );
};

const SummaryTile = ({ icon: Icon, label, value }) => (
  <div className="rounded-md border border-gray-200 bg-white p-4">
    <div className="flex items-center justify-between">
      <p className="text-sm font-medium text-gray-600">{label}</p>
      <Icon className="h-5 w-5 text-gray-400" />
    </div>
    <p className="mt-3 text-xl font-semibold text-gray-900">{value}</p>
  </div>
);

const ContractModal = ({ buildingId, onClose, onSubmit, isSaving }) => {
  const [form, setForm] = useState({
    building_id: buildingId,
    vendor_name: '',
    category: '정기점검',
    title: '',
    contract_start_date: new Date().toISOString().slice(0, 10),
    recurring_interval: 'monthly',
    payment_day: 1,
    amount: '',
    vat_amount: 0,
    auto_generate_expense: true,
    status: 'active',
  });

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Modal title="정기 운영계약 등록" onClose={onClose}>
      <FormGrid>
        <Input label="업체명" value={form.vendor_name} onChange={(value) => update('vendor_name', value)} />
        <Select label="항목" value={form.category} options={categories} onChange={(value) => update('category', value)} />
        <Input label="계약명" value={form.title} onChange={(value) => update('title', value)} wide />
        <Input label="시작일" type="date" value={form.contract_start_date} onChange={(value) => update('contract_start_date', value)} />
        <Select label="집행주기" value={form.recurring_interval} options={intervalOptions} onChange={(value) => update('recurring_interval', value)} />
        <Input label="지급일" type="number" value={form.payment_day} onChange={(value) => update('payment_day', Number(value))} />
        <Input label="공급가" type="number" value={form.amount} onChange={(value) => update('amount', value)} />
        <Input label="부가세" type="number" value={form.vat_amount} onChange={(value) => update('vat_amount', value)} />
      </FormGrid>
      <ModalActions onClose={onClose} isSaving={isSaving} onSave={() => onSubmit(form)} />
    </Modal>
  );
};

const ExpenseModal = ({ buildingId, selectedMonth, onClose, onSubmit, isSaving }) => {
  const [form, setForm] = useState({
    building_id: buildingId,
    source_type: 'manual',
    expense_month: selectedMonth,
    expense_date: `${selectedMonth}-01`,
    category: '수리',
    title: '',
    vendor_name: '',
    amount: '',
    vat_amount: 0,
    burden_type: 'rmc',
    payment_status: 'scheduled',
  });

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Modal title="운영비 등록" onClose={onClose}>
      <FormGrid>
        <Select label="항목" value={form.category} options={categories} onChange={(value) => update('category', value)} />
        <Input label="비용명" value={form.title} onChange={(value) => update('title', value)} />
        <Input label="업체명" value={form.vendor_name} onChange={(value) => update('vendor_name', value)} />
        <Input label="집행일" type="date" value={form.expense_date} onChange={(value) => update('expense_date', value)} />
        <Input label="공급가" type="number" value={form.amount} onChange={(value) => update('amount', value)} />
        <Input label="부가세" type="number" value={form.vat_amount} onChange={(value) => update('vat_amount', value)} />
        <Select
          label="지급상태"
          value={form.payment_status}
          options={[
            { value: 'scheduled', label: '예정' },
            { value: 'pending', label: '미지급' },
            { value: 'paid', label: '지급완료' },
            { value: 'hold', label: '보류' },
          ]}
          onChange={(value) => update('payment_status', value)}
        />
      </FormGrid>
      <ModalActions onClose={onClose} isSaving={isSaving} onSave={() => onSubmit(form)} />
    </Modal>
  );
};

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4">
    <div className="w-full max-w-2xl rounded-md bg-white p-6 shadow-xl">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
          닫기
        </button>
      </div>
      {children}
    </div>
  </div>
);

const FormGrid = ({ children }) => (
  <div className="grid gap-4 md:grid-cols-2">{children}</div>
);

const Input = ({ label, value, onChange, type = 'text', wide = false }) => (
  <label className={wide ? 'md:col-span-2' : ''}>
    <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
    />
  </label>
);

const Select = ({ label, value, options, onChange }) => (
  <label>
    <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
    >
      {options.map((option) => {
        const item = typeof option === 'string' ? { value: option, label: option } : option;
        return (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        );
      })}
    </select>
  </label>
);

const ModalActions = ({ onClose, isSaving, onSave }) => (
  <div className="mt-6 flex justify-end gap-2">
    <button
      type="button"
      onClick={onClose}
      className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
    >
      취소
    </button>
    <button
      type="button"
      onClick={onSave}
      disabled={isSaving}
      className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
    >
      <Plus className="mr-2 h-4 w-4" />
      저장
    </button>
  </div>
);

export default BuildingOperatingExpenses;
